import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ApplyAssistantDraftRequest,
  ApplyAssistantDraftResponse,
  AssistantBlock,
  AssistantConversation,
  AssistantReply,
  SendAssistantMessageRequest,
} from "@/generated/backend/v1/assistant";
import { onSaved } from "@/api/queries";
import { fetchApi } from "@/api/fetchApi";
import { useAuth } from "@/auth/AuthProvider";

/**
 * AbiChat's server state (POST /api/assistant/*). The open conversation is one query entry; replies
 * append their turns to it and replace the blocks they changed, so a reload (GET the latest
 * conversation) shows the same thing.
 */
export const assistantKey = (uid: string) => ["assistant", uid] as const;

const useUid = () => useAuth().user?.uid ?? null;

/** The latest conversation still kept (empty, no uuid, when there's none), loaded once the panel opens. */
export function useConversation(enabled: boolean) {
  const uid = useUid();
  return useQuery({
    queryKey: assistantKey(uid ?? "-"),
    queryFn: () => fetchApi("/assistant/conversation", { response: AssistantConversation }),
    enabled: enabled && !!uid,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

/** The conversation with a reply's turns added and its changed blocks replaced. */
export function withReply(
  conversation: AssistantConversation | null | undefined,
  reply: AssistantReply,
): AssistantConversation {
  const base =
    conversation && conversation.uuid === reply.conversationUuid
      ? conversation
      : AssistantConversation.create({ uuid: reply.conversationUuid });
  return withBlocks({ ...base, turns: [...base.turns, ...reply.turns] }, reply.updatedBlocks);
}

export function withBlocks(
  conversation: AssistantConversation,
  blocks: AssistantBlock[],
): AssistantConversation {
  if (!blocks.length) return conversation;
  const byUuid = new Map(blocks.map((b) => [b.uuid, b]));
  return {
    ...conversation,
    turns: conversation.turns.map((t) => ({
      ...t,
      blocks: t.blocks.map((b) => byUuid.get(b.uuid) ?? b),
    })),
  };
}

function useUpdate() {
  const uid = useUid();
  const client = useQueryClient();
  return {
    reply: (reply: AssistantReply) =>
      uid &&
      client.setQueryData<AssistantConversation | null>(assistantKey(uid), (c) =>
        withReply(c, reply),
      ),
    blocks: (blocks: AssistantBlock[]) =>
      uid &&
      client.setQueryData<AssistantConversation | null>(assistantKey(uid), (c) =>
        c ? withBlocks(c, blocks) : c,
      ),
    clear: () => uid && client.setQueryData(assistantKey(uid), null),
  };
}

export function useSendMessage() {
  const update = useUpdate();
  return useMutation({
    mutationFn: (req: { conversationUuid: string; text: string; page: string }) =>
      fetchApi("/assistant/messages", {
        body: SendAssistantMessageRequest.create(req),
        request: SendAssistantMessageRequest,
        response: AssistantReply,
      }),
    onSuccess: update.reply,
  });
}

/** Reports a what-if whose job has finished; the reply has its result and Abi's reading of it. */
export function useSimulationFinished() {
  const update = useUpdate();
  return useMutation({
    mutationFn: ({ conversation, scenario }: { conversation: string; scenario: string }) =>
      fetchApi(
        `/assistant/conversations/${encodeURIComponent(conversation)}/simulations/${encodeURIComponent(
          scenario,
        )}/finished`,
        { method: "POST", response: AssistantReply },
      ),
    onSuccess: update.reply,
  });
}

/** Apply: saves the drafted change as Update Data would (409/422 are ApiErrors). */
export function useApplyDraft() {
  const uid = useUid();
  const client = useQueryClient();
  const update = useUpdate();
  return useMutation({
    mutationFn: ({ draft, expectedVersion }: { draft: string; expectedVersion: number }) =>
      fetchApi(`/assistant/drafts/${encodeURIComponent(draft)}/apply`, {
        body: ApplyAssistantDraftRequest.create({ expectedVersion }),
        request: ApplyAssistantDraftRequest,
        response: ApplyAssistantDraftResponse,
      }),
    onSuccess: (res) => {
      if (res.block) update.blocks([res.block]);
      if (uid && res.saved) onSaved(client, uid, res.saved);
    },
  });
}

export function useDiscardDraft() {
  const update = useUpdate();
  return useMutation({
    mutationFn: (draft: string) =>
      fetchApi(`/assistant/drafts/${encodeURIComponent(draft)}/discard`, {
        method: "POST",
        response: AssistantBlock,
      }),
    onSuccess: (block) => update.blocks([block]),
  });
}

/** "Clear": deletes the conversation (and its drafts) now rather than after a week. */
export function useClearConversation() {
  const update = useUpdate();
  return useMutation({
    mutationFn: (uuid: string) =>
      fetchApi(`/assistant/conversations/${encodeURIComponent(uuid)}`, { method: "DELETE" }),
    onSuccess: update.clear,
    onError: update.clear,
  });
}
